/**
 * English - `tipsCommerce`: the ⓘ explanations beside selling-operations
 * fields (orders, customers, discounts, gift cards, shipping). One key per
 * field, `<form>.<field>`, passed as the field's `info`.
 * Conventions: see `common.ts`.
 */
const tipsCommerce: Record<string, string> = {
  'refund.partial':
    'Tick this to refund only part of what the customer paid, for example one damaged item. Leave it off to refund the full amount that is still refundable.',
  'refund.amount':
    'How much to send back to the customer, in rupees. It must be more than zero and no more than the amount still refundable. Example: 499.00',
  'refund.reason':
    'A short note on why you are refunding. It is saved on the order history and passed to Razorpay with the refund. Up to 500 characters. Optional.',

  'create_order.mark_as_paid':
    'Tick this if the customer has already paid you outside the store, for example in cash or by UPI to your phone. Leave it off to record the payment as still due, such as cash on delivery.',
  'create_order.item_name':
    'A name for a line that is not in your catalog, such as a service or a one-off charge. It shows as its own line on the order. Example: Gift wrapping',
  'create_order.item_price':
    'The price of one unit of this custom item, in rupees. Enter 0 for a free add-on. Example: 49.00',
  'create_order.item_quantity':
    'How many units of this custom item to add to the order. Enter a whole number from 1 to 99.',
  'create_order.discount_kind':
    'Choose how to take money off this order. Percent reduces the subtotal by a share, while Amount takes off a fixed sum in rupees. No discount code is needed.',
  'create_order.discount_percent':
    'The share of the subtotal to take off, as a number above 0 and up to 100. Example: 10 gives 10% off.',
  'create_order.discount_amount':
    'The fixed sum in rupees to take off this order. It must be more than zero. Example: 150',
  'create_order.shipping':
    'The delivery charge to add to this order, in rupees. Leave it at 0.00 for free delivery or a pickup. Example: 60.00',
  'create_order.customer_name':
    'Full name of the new customer placing this order. Use 2 to 120 characters. Example: Priya Sharma',
  'create_order.customer_email':
    "The new customer's email address. If a customer with this email already exists, the order is linked to them instead of creating a duplicate.",
  'create_order.customer_phone':
    'An Indian mobile number to reach the customer, with or without +91. Optional. Example: +91 98765 43210',
  'create_order.note':
    'A private note about this order, such as how it was placed or a special request. Only you and your staff can see it. Up to 1000 characters. Optional.',

  'customer.first_name':
    "The customer's first or given name, as it should appear on orders and messages. Up to 60 characters. Example: Priya",
  'customer.last_name':
    "The customer's surname or family name. Up to 60 characters. Optional. Example: Sharma",
  'customer.email':
    "The customer's email address. It is how their record is matched at checkout and on import, so it cannot be changed after the customer is added.",
  'customer.phone':
    'An Indian mobile number, with or without +91. Used to contact them, including on WhatsApp. Optional. Example: +91 98765 43210',
  'customer.marketing_opt_in':
    'Tick this only if the customer agreed to get offers and news from you. When on, they can be included in your marketing emails and WhatsApp campaigns.',
  'customer.address':
    'House or flat number, building and street for deliveries. Use 3 to 180 characters. Example: 12 MG Road',
  'customer.address2':
    'Extra details that help the courier find the place, such as apartment, floor or landmark. Up to 180 characters. Optional. Example: Flat 4B, near City Mall',
  'customer.city':
    'The town or city for delivery. Use 2 to 80 characters. Example: Bengaluru',
  'customer.state':
    'The state or union territory for delivery. Use 2 to 80 characters. Example: Karnataka',
  'customer.postal_code':
    'The 6-digit Indian PIN code of the delivery address. It cannot start with 0. Example: 560001',
  'customer.address_name':
    "Who the courier should ask for at this address. Leave blank to use the customer's own name. Up to 120 characters. Example: Reception desk",
  'customer.address_phone':
    "The number the courier should call on delivery, with or without +91. Leave blank to use the customer's own phone. Example: +91 98765 43210",
  'customer.address_default':
    "The default address is prefilled at checkout and shown as the customer's location in your list. Only one address can be the default.",
  'customer.notes':
    'Private notes about this customer, such as preferences or past issues. Customers never see them. Up to 2000 characters. Optional.',
  'customer.tags':
    'Short labels to group customers, so you can find and target them later. Press Enter or type a comma after each one. Up to 30 tags of 40 characters each. Example: wholesale',

  'discount.method':
    'How customers get this discount. Discount code means they must type a code at checkout. Automatic means it applies by itself to every cart that qualifies, with no code.',
  'discount.code':
    'The code customers type at checkout to get this discount. Use 3 to 40 letters, numbers, hyphens or underscores; it is saved in capitals. Example: DIWALI20',
  'discount.title':
    'An internal name so you and your staff can find this discount in your list. Use 2 to 200 characters. Example: Diwali weekend sale',
  'discount.value_type':
    'Percentage takes a share off the price, such as 20% off. Fixed amount takes a set number of rupees off, such as ₹200 off.',
  'discount.percentage_off':
    'How much to take off, as a percentage. Enter a number above 0 and up to 100. Example: 15',
  'discount.amount_off':
    'How many rupees to take off. It must be more than zero. Example: 200',
  'discount.applies_to':
    'Choose which items the discount can lower the price of: all products, only the products you pick, or only products in the collections you pick.',
  'discount.buy_quantity':
    'How many qualifying items the customer must add to their cart before the offer starts. Enter a whole number from 1 to 999. Example: 2',
  'discount.buy_amount':
    'How much the customer must spend on qualifying items, in rupees, before the offer starts. Example: 999',
  'discount.buy_items_from':
    'Which products count towards the purchase requirement. Pick specific products or whole collections.',
  'discount.get_quantity':
    'How many items the customer gets at the discounted value once they meet the requirement. Enter a whole number from 1 to 999. Example: 1',
  'discount.get_items_from':
    'Which products the customer can get at the discounted value. Pick specific products or whole collections. Example: any item from your Socks collection',
  'discount.get_reward':
    'How much the customer saves on the items they get: a percentage off, a fixed amount off each item, or completely free.',
  'discount.get_percentage_off':
    'The percentage taken off each item the customer gets. Enter a number above 0 and up to 100. Example: 50 for half price',
  'discount.get_amount_off':
    'The rupees taken off each item the customer gets. It must be more than zero. Example: 100',
  'discount.cap_uses_per_order':
    'Turn this on to limit how many times the offer can apply within a single order. When off, there is no limit per order.',
  'discount.max_uses_per_order':
    'The most times the offer can apply in one order. Enter a whole number from 1 to 999. Example: 1',
  'discount.countries':
    'Choose where free shipping is offered: to every country you ship to, or only to the countries you tick.',
  'discount.cap_shipping_rate':
    'Turn this on so free shipping does not apply to expensive delivery options. Shipping rates above the amount you set are charged as usual.',
  'discount.max_shipping_amount':
    'The highest shipping rate, in rupees, that this discount will make free. It must be more than zero. Example: 100',
  'discount.eligibility':
    'Choose who can use this discount: every customer, or only the customers you pick. It is checked at checkout, once the customer has identified themselves.',
  'discount.minimum':
    'Optionally ask for a minimum spend or a minimum number of items before the discount applies. You can set one or the other, not both.',
  'discount.min_amount':
    'The least the customer must spend, in rupees, before this discount applies. Example: 1499',
  'discount.min_quantity':
    'The least number of items the customer must have in the cart before this discount applies. Enter a whole number from 1 to 999. Example: 3',
  'discount.cap_total_uses':
    'Turn this on to stop the discount after it has been used a set number of times across all customers. Useful for offers like first 100 orders only.',
  'discount.total_uses':
    'How many times in total this discount can be used before it stops working. Enter a whole number of 1 or more. Example: 100',
  'discount.once_per_customer':
    'When on, each customer can use this code only once. Only available for discount codes, not automatic discounts.',
  'discount.combinations':
    'Choose which other kinds of discounts can be used in the same cart as this one. Both discounts must allow the combination for them to apply together.',
  'discount.start_date':
    'The day the discount starts working. Pick today to start it straight away. Example: 20/10/2026',
  'discount.start_time':
    'The time on the start date when the discount begins, in the time zone shown in the label. Example: 00:00 to start at midnight',
  'discount.set_end':
    'Turn this on to give the discount an end date and time. When off, it keeps running until you turn it off yourself.',
  'discount.end_date':
    'The last day the discount can be used. It cannot be before the start date. Example: 27/10/2026',
  'discount.end_time':
    'The time on the end date when the discount stops, in the time zone shown in the label. Example: 23:59',
  'discount.feature_on_channels':
    'Turn this on, then tick the sales channels below, such as your Online Store, where this discount may be featured.',
  'discount.tags':
    'Labels to help you group and find discounts in your list, such as festival or clearance. Customers do not see them. Up to 50 tags.',

  'gift_card.value':
    'How much the gift card is worth, in rupees. Customers can spend this balance at checkout. It must be more than zero and at most ₹10,00,000. Example: 2000',
  'gift_card.code':
    'The code customers enter at checkout to use the card. Leave it blank to have one created for you, or type 8 to 32 letters and numbers. Spaces and hyphens are removed. Example: GIFTPRIYA2026',
  'gift_card.expires_on':
    'The last day the card can be used; it stays valid until the end of that day. Leave it blank for a card that never expires. Optional.',
  'gift_card.find_customer':
    'Type a name or email to narrow the customer list next to it. It only helps you find the person; nothing typed here is saved.',
  'gift_card.customer':
    'Link the card to one of your customers, for example the person you are giving it to. Choose No customer to leave it unlinked. Optional.',
  'gift_card.note':
    'A private note about this card, such as why it was issued. Only your team sees it. Up to 2000 characters. Optional. Example: Apology for delayed order #1042',

  'shipping_settings.enabled':
    'When on, customers are shown a delivery charge at checkout using the rules on this page. When off, no delivery charge is quoted, though you can still ship orders.',
  'shipping_settings.provider':
    'The courier service that gives live rates and books your parcels. Choose None if you send parcels yourself and only want to note the tracking details.',
  'shipping_settings.pickup_contact_name':
    'The person or business name the courier asks for when collecting parcels. Use 2 to 120 characters. Example: Sharma Handicrafts',
  'shipping_settings.pickup_phone':
    'A number the courier can call when they arrive for pickup. Use 8 to 15 digits; spaces, hyphens and a leading + are fine. Example: +91 98765 43210',
  'shipping_settings.pickup_email':
    'An email address for pickup updates from the courier. Optional. Example: dispatch@yourshop.in',
  'shipping_settings.pickup_line1':
    'House or shop number, building and street where parcels are collected. Use 3 to 180 characters. Example: 45 Linking Road',
  'shipping_settings.pickup_line2':
    'Area, floor or landmark to help the courier find you. Up to 180 characters. Optional. Example: Near Bandra station',
  'shipping_settings.pickup_city':
    'The city where parcels are collected. Use 2 to 80 characters. Example: Mumbai',
  'shipping_settings.pickup_state':
    'The state or union territory of the pickup address. Use 2 to 80 characters. Example: Maharashtra',
  'shipping_settings.pickup_postal_code':
    'The 6-digit PIN code of the pickup address. Couriers use it to work out rates and which services reach you. Example: 400050',
  'shipping_settings.pickup_country':
    'The country of the pickup address. Leave it as India unless your parcels leave from elsewhere. Example: India',
  'shipping_settings.pricing':
    'How the delivery charge at checkout is worked out. Courier rates uses the live price for the customer’s PIN code, Flat rate charges the same on every order, and Free delivery charges nothing.',
  'shipping_settings.markup':
    'Add something on top of the courier’s price to cover your costs. Choose None to pass the courier rate on as it is, or add a percentage or a fixed rupee amount.',
  'shipping_settings.markup_percent':
    'The percentage added on top of the courier’s rate. Enter up to 2 decimal places, and no more than 500. Example: 10 turns a ₹60 rate into ₹66',
  'shipping_settings.markup_amount':
    'The rupees added on top of every courier rate. Enter up to 2 decimal places. Example: 20.00',
  'shipping_settings.handling_fee':
    'A fixed charge in rupees added to every paid delivery quote, for packing and materials. Enter 0 for none. Example: 15.00',
  'shipping_settings.round':
    'Rounds the delivery charge customers see up to a neater number, such as ₹63.40 to ₹65 when rounding up to the next ₹5.',
  'shipping_settings.courier_selection':
    'Which courier to use when several can deliver to a PIN code. Pick cheapest, fastest or best rated, or let the customer choose from a short list at checkout.',
  'shipping_settings.max_options':
    'How many courier options customers see at checkout when they choose for themselves. Enter a whole number from 1 to 6. Example: 3',
  'shipping_settings.fallback':
    'What happens if the courier cannot give a price, for example when their service is down. Charge a flat rate so the order goes through, or stop the order from being placed.',
  'shipping_settings.fallback_rate':
    'The delivery charge in rupees used when the courier cannot give a price. Enter up to 2 decimal places. Example: 80.00',
  'shipping_settings.flat_rate':
    'The delivery charge in rupees added to every order, wherever it is going. Enter up to 2 decimal places. Example: 50.00',
  'shipping_settings.free_above':
    'Orders worth this much or more, after discounts, get free delivery. Leave it blank if delivery is never free. Optional. Example: 999',
  'shipping_settings.handling_days':
    'Days you need to pack an order before it is handed to the courier. They are added to the delivery estimate customers see. Enter 0 to 14. Example: 1',
  'shipping_settings.cod_allow':
    'When on, cash on delivery can be offered through your courier. Customers only see it if cash on delivery is also turned on in your checkout options.',
  'shipping_settings.cod_serviceable':
    'When on, cash on delivery is hidden for PIN codes where the courier will not collect cash. Turn it off only if you will collect the cash yourself there. Needs courier rates.',
  'shipping_settings.parcel_weight':
    'The weight in grams assumed for a parcel when products do not give their own. Enter a whole number from 1 to 100000. Example: 500',
  'shipping_settings.parcel_length':
    'The length of your usual box in centimetres. Couriers charge by actual or size-based weight, whichever is more, so a realistic size matters. Example: 15',
  'shipping_settings.parcel_breadth':
    'The width of your usual box in centimetres, used to estimate courier rates. Example: 12',
  'shipping_settings.parcel_height':
    'The height of your usual box in centimetres, used to estimate courier rates. Example: 8',
  'shipping_settings.auto_book':
    'When on, each confirmed order is booked with the courier the customer was quoted, without you doing it by hand. Needs a courier provider.',
  'shipping_settings.auto_pickup':
    'When on, the courier is asked to collect the parcel as soon as the booking is made. Needs a courier provider.',

  'shipment.mode':
    'Book with courier gets live rates and books through your connected courier. Record manually is for parcels you sent another way, such as India Post, so you only note the details.',
  'shipment.weight':
    'The packed weight of this parcel in whole grams, used for the courier price. Leave it blank to use your default parcel weight. Example: 750',
  'shipment.request_pickup':
    'Tick this to ask the courier to collect the parcel as soon as it is booked. Leave it off if you want to request pickup later.',
  'shipment.carrier':
    'The name of the courier or service carrying this parcel. Up to 80 characters. Example: India Post',
  'shipment.tracking_number':
    'The tracking or AWB number the courier gave you. Up to 64 characters. Optional. Example: EK123456789IN',
  'shipment.tracking_url':
    'A web link where the parcel can be tracked. Customers are shown it. Up to 500 characters. Optional. Example: https://www.indiapost.gov.in',
  'shipment.cancel_note':
    'A short note on why this shipment is being cancelled, kept on its history. Up to 300 characters. Optional. Example: Customer changed the address',
  'shipment.status':
    'Move the shipment to a new stage, such as In transit or Delivered. Use it for parcels you track yourself, or when the courier updates are running late.',
  'shipment.status_note':
    'A short note saved with this status change on the shipment history. Up to 300 characters. Optional. Example: Handed to courier at 4 pm',

  // >>> QA phase 5 additions
  'discount.eligibility_all': 'Anyone who shops with you can use this discount.',
  'discount.eligibility_customers':
    'Only the customers you pick here can use it. Others are told the code does not apply.',
  'discount.minimum_none': 'The discount applies to any basket, however small.',
  'discount.minimum_amount':
    'The basket must reach this amount, before shipping, for the discount to apply.',
  'discount.minimum_quantity':
    'The basket must hold at least this many items for the discount to apply.',
  'discount.combines_product':
    'Ticked, this discount can be used together with a product discount on the same order.',
  'discount.combines_order':
    'Ticked, this discount can be used together with an order discount on the same order.',
  'discount.combines_shipping':
    'Ticked, this discount can be used together with a free or reduced shipping discount.',
  'discount.add_tags':
    'Your own labels for finding and grouping discounts. Separate several with commas.',
  'shipping_settings.credential_email':
    'The email of an API user you create in Shiprocket under Settings → API → Configure. Not your Shiprocket login.',
  'shipping_settings.credential_password':
    'The password you gave that API user in Shiprocket. It is stored encrypted and never shown again.',
  'shipping_settings.credential_webhook_secret':
    'Any long random string. Paste the same value into Shiprocket’s webhook settings as the x-api-key token, so delivery updates can be trusted.',
  'shipping_settings.credential_unknown':
    'A detail your shipping provider needs to connect. Copy it from your account with them.',
  'shipping_settings.mode_provider':
    'Shoppers pay what the courier quotes for their PIN code, plus any markup you set.',
  'shipping_settings.mode_flat': 'Every order pays the same delivery charge, wherever it goes.',
  'shipping_settings.mode_free': 'Delivery is free on every order; you cover the courier cost.',
};

export default tipsCommerce;
